import React, { useState, useEffect } from 'react'
import { toastError, toastSuccess, toastLoading, toastDismiss } from '../../utils/toastHelper'
import { useReservaDetail, QUERY_KEYS } from '../../hooks/useReservationsAdmin'
import { usePagosAdmin, type Pago } from '../../hooks/usePagosAdmin'
import { useTheme } from '../../hooks/useTheme'
import { useDeviceDetection } from '../../hooks/useDeviceDetection'
import { useApiClient } from '../../hooks/useApiClient'
import { useAuth } from '../../context/AuthContext'
import { useQueryClient } from '@tanstack/react-query'
import { getStatusColors } from '../../utils/statusColorMap'
import { formatDateTimeDisplay, formatDateDisplay, parseLocalDateString, calcularUnidades } from '../../utils/dateHelpers'
import { formatCurrency } from '../../utils/currencyHelpers'
import EditarClienteReservaModal from './EditarClienteReservaModal'
import ConfirmDeleteReservaModal from './ConfirmDeleteReservaModal'
import CambiarReservaModal from './CambiarReservaModal'
import ComprobanteViewer from '../common/ComprobanteViewer'

import type { Reserva } from '../../hooks/useReservationsAdmin'

interface ReservaDetailModalProps {
  reservaId?: string
  isOpen: boolean
  onClose: () => void
}

const ReservaDetailModal: React.FC<ReservaDetailModalProps> = ({
  reservaId,
  isOpen,
  onClose,
}) => {
  const { data: reserva, isLoading, isError, refetch } = useReservaDetail(reservaId)
  const { colors } = useTheme()
  const deviceInfo = useDeviceDetection()
  const { apiClient } = useApiClient()
  const { permissions } = useAuth()
  const queryClient = useQueryClient()

  const hasPermission = (permission: string): boolean => {
    return permissions?.includes(permission) || false
  }

  // Estado para el modal de pagos (anidado)
  const [isPagosModalOpen, setIsPagosModalOpen] = useState(false)
  const [selectedPago, setSelectedPago] = useState<Pago | undefined>()
  const [isPagoDetalleModalOpen, setIsPagoDetalleModalOpen] = useState(false)

  // Estado para reenviar correo
  const [isReenviadoCorreoLoading, setIsReenviadoCorreoLoading] = useState(false)

  // Estado para editar cliente
  const [isEditarClienteModalOpen, setIsEditarClienteModalOpen] = useState(false)

  // Estado para cambiar reserva
  const [isCambiarReservaModalOpen, setIsCambiarReservaModalOpen] = useState(false)

  // Estado para eliminar reserva
  const [isDeletingReserva, setIsDeletingReserva] = useState(false)
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false)

  // Estado para confirmar/desconfirmar reserva (sin pago)
  const [isConfirmingReserva, setIsConfirmingReserva] = useState(false)

  // Estado para validación de disponibilidad antes de confirmar
  const [disponibilidadValida, setDisponibilidadValida] = useState(true)
  const [validandoDisponibilidad, setValidandoDisponibilidad] = useState(false)
  const [mensajeDisponibilidad, setMensajeDisponibilidad] = useState('')

  // Obtener pagos de esta reserva
  const { data: pagosResponse } = usePagosAdmin(
    reservaId ? { buscar: reservaId, limit: 100, offset: 0 } : { limit: 100, offset: 0 }
  )
  const pagos = pagosResponse?.pagos || []

  // Manejar Escape para cerrar modales en jerarquía inversa
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        // Cerrar en orden inverso de apertura
        if (isPagoDetalleModalOpen) {
          setIsPagoDetalleModalOpen(false)
        } else if (isPagosModalOpen) {
          setIsPagosModalOpen(false)
        } else if (isConfirmDeleteOpen) {
          setIsConfirmDeleteOpen(false)
        } else if (isEditarClienteModalOpen) {
          setIsEditarClienteModalOpen(false)
        } else if (isCambiarReservaModalOpen) {
          setIsCambiarReservaModalOpen(false)
        } else {
          onClose()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, isPagoDetalleModalOpen, isPagosModalOpen, isConfirmDeleteOpen, isEditarClienteModalOpen, isCambiarReservaModalOpen, onClose])

  // ✅ Validar disponibilidad antes de permitir confirmar reserva
  useEffect(() => {
    if (!isOpen || !reserva || reserva.estado === 'confirmada') {
      // No necesita validar si ya está confirmada
      setDisponibilidadValida(true)
      return
    }

    const validarDisponibilidad = async () => {
      try {
        setValidandoDisponibilidad(true)

        // Obtener espacios de esta reserva (usar 'espacios' que es el campo de la API)
        const espacios = reserva.espacios || []
        if (espacios.length === 0) {
          setDisponibilidadValida(false)
          setMensajeDisponibilidad('No hay espacios asociados a esta reserva')
          return
        }

        // Validar cada espacio individualmente
        for (const espacio of espacios) {
          // Usar las fechas individuales del espacio si existen, sino usar las globales
          const fechaEntrada = espacio.fecha_entrada || reserva.fecha_entrada
          const fechaSalida = espacio.fecha_salida || reserva.fecha_salida

          const response = await apiClient.get(
            `/api/espacios/rev_disponibilidad_espacio/${espacio.id}?fecha_entrada=${fechaEntrada}&fecha_salida=${fechaSalida}`
          )

          // ✅ axios retorna response.data, no response directamente
          if (!response.data.disponible) {
            setDisponibilidadValida(false)
            setMensajeDisponibilidad(`${espacio.nombre || 'Espacio'}: No hay disponibilidad en ese rango`)
            return
          }
        }

        // Todos los espacios están disponibles
        setDisponibilidadValida(true)
        setMensajeDisponibilidad('')
      } catch (error: any) {
        console.error('Error validando disponibilidad:', error)
        setDisponibilidadValida(false)
        setMensajeDisponibilidad('Error al validar disponibilidad')
      } finally {
        setValidandoDisponibilidad(false)
      }
    }

    validarDisponibilidad()
  }, [isOpen, reserva, apiClient])

  if (!isOpen) return null

  const handleReenviarCorreo = async () => {
    if (!hasPermission('reservas.ver')) {
      toastError('No tienes permisos para reenviar correos')
      return
    }

    if (!reservaId) {
      toastError('ID de reserva no disponible')
      return
    }

    setIsReenviadoCorreoLoading(true)
    const toastId = toastLoading('📧 Reenviando correo de confirmación...')

    try {
      const result = await apiClient.post(
        `/api/admin-protected/reservas/${reservaId}/reenviar-correo`
      )
      toastSuccess(`✅ Correo reenviado exitosamente a ${result.data.cliente_email}`, { id: toastId })
    } catch (error: any) {
      console.error('Error reenviar correo:', error)
      toastError(error.response?.data?.detail || 'Error al reenviar correo', { id: toastId })
    } finally {
      setIsReenviadoCorreoLoading(false)
    }
  }

  const handleAbrirConfirmDelete = () => {
    if (!hasPermission('reservas.eliminar')) {
      toastError('No tienes permisos para eliminar reservas')
      return
    }

    if (!reservaId) {
      toastError('ID de reserva no disponible')
      return
    }

    // Validar que no hay pagos asociados
    if (pagos.length > 0) {
      toastError('⚠️ No se puede eliminar una reserva con pagos asociados')
      return
    }

    setIsConfirmDeleteOpen(true)
  }

  const handleConfirmarReserva = async (confirmar: boolean) => {
    if (!hasPermission('reservas.confirmar')) {
      toastError('No tienes permisos para confirmar reservas')
      return
    }

    if (!reservaId) {
      toastError('ID de reserva no disponible')
      return
    }

    setIsConfirmingReserva(true)
    const accion = confirmar ? 'confirmando' : 'desconfirmando'
    const toastId = toastLoading(`⏳ ${accion} reserva...`)

    try {
      await apiClient.put(
        `/api/admin-protected/reservas/${reservaId}/confirmar?confirmar=${confirmar}`
      )

      const mensaje = confirmar ? '✅ Reserva confirmada exitosamente' : '✅ Reserva volvió a pendiente'
      toastSuccess(mensaje, { id: toastId })

      // Refrescar datos
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.RESERVA_DETAIL, reservaId],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.RESERVAS_ADMIN],
      })
    } catch (error: any) {
      console.error(`Error ${accion} reserva:`, error)
      toastError(error.response?.data?.detail || `Error al ${accion} la reserva`, { id: toastId })
    } finally {
      setIsConfirmingReserva(false)
    }
  }

  const handleConfirmDeleteReserva = async () => {
    if (!hasPermission('reservas.eliminar')) {
      toastError('No tienes permisos para eliminar reservas')
      return
    }

    if (!reservaId) {
      toastError('ID de reserva no disponible')
      return
    }

    setIsDeletingReserva(true)
    const toastId = toastLoading('🗑️ Eliminando reserva...')

    try {
      await apiClient.delete(
        `/api/admin-protected/reservas/${reservaId}?forzar=true`
      )
      toastSuccess('✅ Reserva eliminada exitosamente', { id: toastId })

      // Cerrar el modal ANTES de invalidar caches
      // (para evitar intentar recargar una reserva eliminada)
      setIsConfirmDeleteOpen(false)
      onClose()

      // Invalidar solo la lista (la reserva ya no existe, así que no la detalle)
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.RESERVAS_ADMIN],
      })
    } catch (error: any) {
      console.error('Error eliminando reserva:', error)
      toastError(error.response?.data?.detail || 'Error al eliminar la reserva', { id: toastId })
      setIsConfirmDeleteOpen(false)
    } finally {
      setIsDeletingReserva(false)
    }
  }

  const getEstadoColor = (estado: string) => {
    let statusType: 'confirmed' | 'pending' | 'cancelled' | 'completed' = 'pending'

    switch (estado) {
      case 'confirmada':
        statusType = 'confirmed'
        break
      case 'completada':
        statusType = 'completed'
        break
      case 'cancelada':
        statusType = 'cancelled'
        break
      case 'en_proceso':
      case 'pendiente':
        statusType = 'pending'
        break
    }

    const statusColors = getStatusColors(statusType, colors)
    return {
      backgroundColor: statusColors.lightBackground,
      color: statusColors.backgroundColor
    }
  }

  const getEstadoPagoColor = (estado: string) => {
    let statusType: 'confirmed' | 'pending' | 'cancelled' = 'pending'

    switch (estado) {
      case 'pagado':
        statusType = 'confirmed'
        break
      case 'reembolsado':
        statusType = 'cancelled'
        break
      case 'pendiente':
        statusType = 'pending'
        break
    }

    const statusColors = getStatusColors(statusType, colors)
    return {
      backgroundColor: statusColors.lightBackground,
      color: statusColors.backgroundColor
    }
  }

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-start justify-center min-h-full pt-4 px-4 sm:pt-16">
          <div className={`bg-white rounded-lg shadow-xl w-full overflow-hidden ${
            deviceInfo.isMobile
              ? 'max-w-full'
              : deviceInfo.isTablet
              ? 'max-w-xl'
              : 'max-w-2xl'
          }`}>
            {/* Header */}
            <div className="px-6 py-4 flex items-center justify-between text-white" style={{
              background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryDark})`
            }}>
              <h2 className="text-xl font-bold">
                Detalle de Reserva
              </h2>
              <div className="flex items-center gap-3">
                {hasPermission('pagos.ver') && (
                  <button
                    onClick={() => setIsPagosModalOpen(true)}
                    className="text-white text-xl transition-opacity hover:opacity-80"
                    title="Ver pagos de esta reserva"
                  >
                    💳
                  </button>
                )}
                {hasPermission('reservas.ver') && (
                  <button
                    onClick={handleReenviarCorreo}
                    disabled={isReenviadoCorreoLoading}
                    className="text-white text-xl transition-opacity hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Reenviar correo de confirmación"
                  >
                    {isReenviadoCorreoLoading ? '⏳' : '📧'}
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="text-white transition-colors"
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin h-8 w-8 border-2 border-t-transparent rounded-full" style={{
                    borderColor: colors.primary,
                    borderTopColor: 'transparent'
                  }}></div>
                </div>
              ) : isError ? (
                <div className="py-4" style={{ color: colors.error }}>
                  Error al cargar los detalles de la reserva
                </div>
              ) : reserva ? (
                <div className="space-y-4">
                  {/* Sección: Información General */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      Información General
                    </h3>
                    <div className={`grid gap-4 ${
                      deviceInfo.isMobile ? 'grid-cols-1' : 'grid-cols-2'
                    }`}>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Código de Reserva</p>
                        <p className="text-sm font-medium text-gray-900">
                          {reserva.codigo || reserva.id || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Fecha Creación</p>
                        <p className="text-sm font-medium text-gray-900">
                          {(() => {
                            const dateStr = typeof reserva.fecha_creacion === 'string'
                              ? reserva.fecha_creacion.split('T')[0]
                              : reserva.fecha_creacion;
                            return parseLocalDateString(dateStr).toLocaleDateString('es-CL');
                          })()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Sección: Cliente */}
                  <div className="border-t border-gray-200 pt-3">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      Información del Cliente
                    </h3>
                    <div className={`grid gap-4 ${
                      deviceInfo.isMobile ? 'grid-cols-1' : 'grid-cols-3'
                    }`}>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Nombre Completo</p>
                        <p className="text-sm font-medium text-gray-900">
                          {reserva.cliente_nombre || reserva.huesped_nombre || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Email</p>
                        <p className="text-sm font-medium text-gray-900">
                          {reserva.cliente_email || reserva.huesped_email || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Teléfono</p>
                        <p className="text-sm font-medium text-gray-900">
                          {reserva.cliente_telefono || reserva.huesped_telefono || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Sección: Espacios CON fechas individuales */}
                  <div className="border-t border-gray-200 pt-3">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      Espacios
                    </h3>
                    {reserva.espacios && reserva.espacios.length > 0 ? (
                      <div className="space-y-3">
                        {reserva.espacios.map((espacio, idx) => {
                          const fechaEntrada = espacio.fecha_entrada || reserva.fecha_entrada
                          const fechaSalida = espacio.fecha_salida || reserva.fecha_salida

                          return (
                            <div
                              key={idx}
                              className="p-4 border border-gray-200 rounded-lg"
                              style={{ backgroundColor: `${colors.primary}08` }}
                            >
                              {/* Nombre y código del espacio */}
                              <div className="flex items-center justify-between mb-3">
                                <div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {espacio.nombre}
                                  </p>
                                  {espacio.codigo && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      Código: {espacio.codigo}
                                    </p>
                                  )}
                                </div>
                                {espacio.tarifa_unitaria && (
                                  <p className="text-sm font-semibold text-gray-900">
                                    {formatCurrency(espacio.tarifa_unitaria)}
                                  </p>
                                )}
                              </div>

                              {/* Fechas e unidades individuales del espacio */}
                              <div className={`grid gap-3 pt-3 border-t border-gray-200 ${
                                deviceInfo.isMobile ? 'grid-cols-2' : 'grid-cols-3'
                              }`}>
                                <div>
                                  <p className="text-xs text-gray-500 uppercase mb-1">Entrada</p>
                                  <p className="text-sm font-medium text-gray-900">
                                    {fechaEntrada ? parseLocalDateString(fechaEntrada).toLocaleDateString('es-CL') : 'N/A'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 uppercase mb-1">Salida</p>
                                  <p className="text-sm font-medium text-gray-900">
                                    {fechaSalida ? parseLocalDateString(fechaSalida).toLocaleDateString('es-CL') : 'N/A'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 uppercase mb-1">Unidades</p>
                                  <p className="text-sm font-medium text-gray-900">
                                    {fechaEntrada && fechaSalida
                                      ? calcularUnidades(fechaEntrada, fechaSalida, espacio.unidad_reserva)
                                      : 'N/A'
                                    }
                                  </p>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600">
                        {reserva.espacio_nombre || 'N/A'}
                      </p>
                    )}
                  </div>

                  {/* Sección: Estados */}
                  <div className="border-t border-gray-200 pt-3">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      Estados
                    </h3>
                    <div className={`grid gap-4 ${
                      deviceInfo.isMobile ? 'grid-cols-1' : 'grid-cols-2'
                    }`}>
                      <div>
                        <p className="text-sm text-gray-600 mb-2">Estado Reserva</p>
                        <span
                          className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full"
                          style={getEstadoColor(reserva.estado)}
                        >
                          {reserva.estado}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-2">Estado Pago</p>
                        <span
                          className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full"
                          style={getEstadoPagoColor(reserva.estado_pago)}
                        >
                          {reserva.estado_pago}
                        </span>
                      </div>
                    </div>

                    {/* Toggle para confirmar/desconfirmar reserva (permite cualquier porcentaje de pago) */}
                    {['pendiente', 'confirmada'].includes(reserva.estado) && (
                      <div className="mt-4 p-4 rounded-lg border" style={{
                        backgroundColor: `${colors.primary}08`,
                        borderColor: `${colors.primary}30`
                      }}>
                        {/* Mensaje de validación de disponibilidad */}
                        {reserva.estado === 'pendiente' && !disponibilidadValida && (
                          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-700 font-medium">⚠️ {mensajeDisponibilidad || 'No hay disponibilidad para confirmar esta reserva'}</p>
                          </div>
                        )}

                        {/* Indicador de validación en progreso */}
                        {reserva.estado === 'pendiente' && validandoDisponibilidad && (
                          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm text-blue-700 font-medium">🔄 Validando disponibilidad...</p>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {reserva.estado === 'pendiente' ? '✓ Confirmar reserva' : '⟲ Volver a pendiente'}
                            </p>
                            <p className="text-xs text-gray-600 mt-1">
                              {reserva.estado === 'pendiente'
                                ? 'Cambiar a confirmada (con cualquier porcentaje de pago)'
                                : 'Cambiar nuevamente a estado pendiente'
                              }
                            </p>
                          </div>
                          {hasPermission('reservas.confirmar') && (
                            <button
                              onClick={() => handleConfirmarReserva(reserva.estado === 'pendiente')}
                              disabled={isConfirmingReserva || (reserva.estado === 'pendiente' && (!disponibilidadValida || validandoDisponibilidad))}
                              className="ml-4 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              style={{
                                backgroundColor: reserva.estado === 'pendiente'
                                  ? (!disponibilidadValida || validandoDisponibilidad ? '#9ca3af' : colors.primary)
                                  : '#6b7280'
                              }}
                              onMouseEnter={(e) => {
                                const isDisabled = isConfirmingReserva || (reserva.estado === 'pendiente' && (!disponibilidadValida || validandoDisponibilidad))
                                if (!isDisabled) {
                                  e.currentTarget.style.opacity = '0.9'
                                }
                              }}
                              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                              title={
                                validandoDisponibilidad
                                  ? 'Validando disponibilidad...'
                                  : !disponibilidadValida && reserva.estado === 'pendiente'
                                  ? mensajeDisponibilidad || 'No hay disponibilidad para confirmar'
                                  : reserva.estado === 'pendiente'
                                  ? 'Confirmar reserva'
                                  : 'Cambiar a pendiente'
                              }
                            >
                              {validandoDisponibilidad ? '🔄' : isConfirmingReserva ? '⏳' : reserva.estado === 'pendiente' ? '✓' : '⟲'}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Sección: Monto */}
                  <div className="border-t border-gray-200 pt-4 bg-gray-50 -mx-6 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <p className="text-lg font-semibold text-gray-900">
                        Monto Total
                      </p>
                      <p className="text-2xl font-bold" style={{ color: colors.primary }}>
                        {formatCurrency(reserva.monto_total)}
                      </p>
                    </div>
                  </div>

                  {/* Sección: Pago (solo reservas manuales) */}
                  {reserva?.tipo_reserva === 'manual' && (
                    <div className="border-t border-gray-200 pt-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Información de Pago</h3>
                      <div className={`grid gap-3 ${deviceInfo.isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Estado</p>
                          <span
                            className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full"
                            style={getEstadoPagoColor(reserva.estado_pago || 'pendiente')}
                          >
                            {reserva.estado_pago || 'pendiente'}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Monto</p>
                          <p className="text-sm font-medium text-gray-900">{formatCurrency(reserva.monto_total)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Sección: Solicitudes Especiales */}
                  {reserva.solicitudes_especiales && (
                    <div className="border-t border-gray-200 pt-3">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Solicitudes Especiales
                      </h3>
                      <p className="text-sm text-gray-700 p-3 rounded" style={{
                        backgroundColor: `${colors.primary}15`
                      }}>
                        {reserva.solicitudes_especiales}
                      </p>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {/* Footer */}
            <div className={`bg-gray-50 px-6 py-4 border-t border-gray-200 ${
              deviceInfo.isMobile
                ? 'flex flex-col gap-3'
                : 'flex justify-between items-center'
            }`}>
              {/* Botón Eliminar */}
              {pagos.length === 0 && hasPermission('reservas.eliminar') && (
                <button
                  onClick={handleAbrirConfirmDelete}
                  disabled={isDeletingReserva}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    deviceInfo.isMobile ? 'w-full' : ''
                  }`}
                  style={{ backgroundColor: '#dc2626' }}
                  onMouseEnter={(e) => !isDeletingReserva && (e.currentTarget.style.opacity = '0.9')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                  title="Eliminar reserva (solo disponible sin pagos)"
                >
                  {isDeletingReserva ? '⏳ Eliminando...' : '🗑️ Eliminar'}
                </button>
              )}

              {/* Botones de Acción */}
              <div className={`flex gap-3 ${
                deviceInfo.isMobile
                  ? 'flex-col w-full'
                  : 'flex-row'
              }`}>
                <button
                  onClick={onClose}
                  className={`px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors ${
                    deviceInfo.isMobile ? 'w-full' : ''
                  }`}
                >
                  Cerrar
                </button>
                {hasPermission('reservas.editar') && reserva && reserva.estado !== 'cancelada' && reserva.estado !== 'completada' && (
                  <button
                    onClick={() => setIsCambiarReservaModalOpen(true)}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                      deviceInfo.isMobile ? 'w-full' : ''
                    }`}
                    style={{ backgroundColor: '#ff9800' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f57c00'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ff9800'}
                    title="Cambiar espacio y/o fechas de la reserva"
                  >
                    🔄 Cambiar Reserva
                  </button>
                )}
                {hasPermission('reservas.editar') && (
                  <button
                    onClick={() => setIsEditarClienteModalOpen(true)}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                      deviceInfo.isMobile ? 'w-full' : ''
                    }`}
                    style={{ backgroundColor: colors.primary }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.primaryDark}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.primary}
                    title="Editar datos del cliente de esta reserva"
                  >
                    ✏️ Editar Cliente
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Pagos (anidado) */}
      {isPagosModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 flex items-center justify-between text-white" style={{
              background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryDark})`
            }}>
              <h2 className="text-lg font-bold">Pagos de la Reserva</h2>
              <button
                onClick={() => setIsPagosModalOpen(false)}
                className="text-white text-2xl font-bold transition-opacity hover:opacity-80"
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              {pagos.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">📭 No hay pagos para esta reserva</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pagos.map((pago) => (
                    <div
                      key={pago.id}
                      className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium text-gray-900">#{pago.id}</span>
                            <span
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                              style={pago.estado === 'aprobado'
                                ? { backgroundColor: '#d1fae5', color: '#047857' }
                                : { backgroundColor: '#fef3c7', color: '#b45309' }
                              }
                            >
                              {pago.estado === 'aprobado' ? '✓' : '⏳'} {pago.estado.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">
                            {formatCurrency(pago.monto)} {pago.moneda}
                          </p>
                          <p className="text-xs text-gray-500">
                            {pago.fecha_creacion
                              ? (() => {
                                  const dateStr = typeof pago.fecha_creacion === 'string'
                                    ? pago.fecha_creacion.split('T')[0]
                                    : pago.fecha_creacion;
                                  return parseLocalDateString(dateStr).toLocaleDateString('es-CL');
                                })()
                              : 'Sin fecha'
                            }
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setSelectedPago(pago)
                              setIsPagoDetalleModalOpen(true)
                            }}
                            className="text-gray-400 hover:text-gray-600 transition-colors text-xl"
                            title="Ver detalles del pago"
                          >
                            🔍
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <button
                onClick={() => setIsPagosModalOpen(false)}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalles del Pago (más anidado) */}
      {isPagoDetalleModalOpen && selectedPago && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 flex items-center justify-between text-white" style={{
              background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryDark})`
            }}>
              <h2 className="text-lg font-bold">Detalles del Pago #{selectedPago.id}</h2>
              <button
                onClick={() => setIsPagoDetalleModalOpen(false)}
                className="text-white text-2xl font-bold transition-opacity hover:opacity-80"
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">ID Pago</p>
                  <p className="font-semibold text-gray-900">#{selectedPago.id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Monto</p>
                  <p className="font-semibold text-gray-900">
                    {formatCurrency(selectedPago.monto)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Estado</p>
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                    style={selectedPago.estado === 'aprobado'
                      ? { backgroundColor: '#d1fae5', color: '#047857' }
                      : { backgroundColor: '#fef3c7', color: '#b45309' }
                    }
                  >
                    {selectedPago.estado.toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Proveedor</p>
                  <p className="font-semibold text-gray-900 capitalize">{selectedPago.proveedor_pago}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Fecha Creación</p>
                  <p className="font-semibold text-gray-900 text-sm">
                    {selectedPago.fecha_creacion
                      ? (() => {
                          const dateStr = typeof selectedPago.fecha_creacion === 'string'
                            ? selectedPago.fecha_creacion.split('T')[0]
                            : selectedPago.fecha_creacion;
                          return parseLocalDateString(dateStr).toLocaleDateString('es-CL');
                        })()
                      : 'Sin fecha'
                    }
                  </p>
                </div>
              </div>

              {/* 📎 Sección de Comprobante de Pago (Solo Lectura) */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  📎 Comprobante de Pago
                </h3>

                {selectedPago.comprobante_path ? (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <ComprobanteViewer
                      pagoId={selectedPago.id}
                      filename={selectedPago.comprobante_filename}
                      mimeType={selectedPago.comprobante_mime_type}
                      size={selectedPago.comprobante_size}
                      uploadDate={selectedPago.comprobante_upload_date}
                      showMetadata={true}
                    />
                  </div>
                ) : (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-center">
                    <p className="text-xs text-gray-600">
                      📭 No hay comprobante
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <button
                onClick={() => setIsPagoDetalleModalOpen(false)}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para editar datos del cliente */}
      <EditarClienteReservaModal
        isOpen={isEditarClienteModalOpen}
        reserva={reserva || null}
        reservaId={reservaId}
        onClose={() => setIsEditarClienteModalOpen(false)}
        onSuccess={() => {
          // Refrescar los datos de la reserva después de editar
          refetch()
        }}
      />

      {/* Modal para cambiar reserva */}
      {reserva && (
        <CambiarReservaModal
          isOpen={isCambiarReservaModalOpen}
          reserva={reserva}
          onClose={() => setIsCambiarReservaModalOpen(false)}
          onSuccess={() => {
            // Refrescar los datos de la reserva después de cambiar
            refetch()
            // Invalidar query de reservas para actualizar lista
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RESERVATIONS] })
          }}
        />
      )}

      {/* Modal de confirmación de eliminación */}
      <ConfirmDeleteReservaModal
        isOpen={isConfirmDeleteOpen}
        reservaId={reservaId || ''}
        isLoading={isDeletingReserva}
        onConfirm={handleConfirmDeleteReserva}
        onCancel={() => setIsConfirmDeleteOpen(false)}
      />
    </>
  )
}

export default ReservaDetailModal
